/**
 * Review and Testimonial Schema Components
 * From SchemaMarkup.tsx lines 419-576
 */

import { BASE_URL, SEO } from '@/lib/constants';
import type { ReviewSchemaProps, TestimonialReview } from './types';
import { parseReviewDate } from './utils';

/**
 * Aggregate Rating Schema
 */
export function AggregateRatingSchema({
  ratingValue,
  reviewCount,
}: {
  ratingValue: number;
  reviewCount: number;
}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': `${BASE_URL}/#organization`,
    name: SEO.siteName,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: ratingValue.toFixed(1),
      bestRating: '5',
      worstRating: '1',
      reviewCount,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

/**
 * Individual Review Schema
 * Critical for AI discovery - LLMs prefer individual reviews
 */
export function ReviewSchema({
  reviewerName,
  reviewerLocation,
  reviewDate,
  ratingValue,
  reviewBody,
  itemReviewed,
}: ReviewSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: reviewerName,
      ...(reviewerLocation && {
        address: {
          '@type': 'PostalAddress',
          addressLocality: reviewerLocation,
        },
      }),
    },
    datePublished: reviewDate,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: ratingValue.toFixed(1),
      bestRating: '5',
      worstRating: '1',
    },
    reviewBody,
    itemReviewed: {
      '@type': 'TravelAgency',
      '@id': `${BASE_URL}/#organization`,
      name: SEO.siteName,
      description: itemReviewed,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

/**
 * Testimonials Page Schema
 * Combines aggregate rating with individual reviews
 */
export function TestimonialsPageSchema({
  reviews,
}: {
  reviews: TestimonialReview[];
}) {
  const avgRating =
    reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': `${BASE_URL}/#organization`,
    name: SEO.siteName,
    description:
      'Premier luxury adventure travel company specializing in heli-skiing, fly fishing, and exclusive lodges in Chile.',
    url: BASE_URL,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: avgRating.toFixed(1),
      bestRating: '5',
      worstRating: '1',
      reviewCount: reviews.length,
    },
    review: reviews.map((review) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.name,
        address: {
          '@type': 'PostalAddress',
          addressLocality: review.location,
        },
      },
      datePublished: parseReviewDate(review.date),
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating.toFixed(1),
        bestRating: '5',
        worstRating: '1',
      },
      reviewBody: review.text,
      name: review.adventure,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
